/**
 * useTeamMembers Hook
 *
 * Handles fetching and managing team members for conversation assignment.
 */

import {useState, useCallback, useMemo, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {TeamMember, ConversationData} from '../types/inbox-v3.types'
import {mapV2TeamMembersToV3} from '../services/inboxService'
import {GetAllTeamList} from '../../accounts/Overview/OverviewSlice/TeamListSlice'
import {PortalNotifiationBySuperAdmin} from '../../../../Redux/AdminPanel/CommentSlice'
import {fetchAsyncUserDetails} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'

interface UseTeamMembersOptions {
  fetchOnMount?: boolean
}

interface AssignConversationParams {
  contactId: string
  receiver: string
  assigneeId: string
}

interface UseTeamMembersReturn {
  // Data
  teamMembers: TeamMember[]
  isLoading: boolean

  // Actions
  refresh: () => void
  assignConversation: (params: AssignConversationParams) => Promise<void>
  unassignConversation: (contactId: string) => Promise<void>
  getAssignedMember: (conversation: ConversationData | null) => TeamMember | null
}

export function useTeamMembers(options: UseTeamMembersOptions = {}): UseTeamMembersReturn {
  const {fetchOnMount = true} = options

  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  // Get current user details
  const userDetails = useSelector((state: any) => state.userDetails.userDetails?.data?.user)
  const OwnerId = userDetails?._id

  // Get team members from Redux store - same as inbox-v2
  // This combines the current user with team list items
  const reduxTeamMembers = useSelector((state: any) => {
    const teamData = state?.teamlist?.TeamList?.items || []
    const user = state.userDetails.userDetails?.data?.user
    // Include current user at the start of the list, like inbox-v2
    if (user) {
      return [user, ...teamData]
    }
    return teamData
  })

  // Transform to inbox-v3 types
  const teamMembers = useMemo<TeamMember[]>(() => {
    if (!reduxTeamMembers || !Array.isArray(reduxTeamMembers) || reduxTeamMembers.length === 0) {
      return []
    }
    return mapV2TeamMembersToV3(reduxTeamMembers)
  }, [reduxTeamMembers])

  // Fetch team members from API - use GetAllTeamList like inbox-v2
  const fetchMembers = useCallback(() => {
    if (!OwnerId) return
    setIsLoading(true)
    dispatch(GetAllTeamList({ownerId: OwnerId}) as any).finally(() => {
      setIsLoading(false)
    })
  }, [dispatch, OwnerId])

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount && OwnerId) {
      fetchMembers()
    }
  }, [fetchOnMount, fetchMembers, OwnerId])

  // Assign conversation to team member - uses same endpoint as inbox-v2
  const assignConversation = useCallback(
    async ({contactId, receiver, assigneeId}: AssignConversationParams) => {
      await dispatch(
        PortalNotifiationBySuperAdmin({
          userTo: [assigneeId],
          message: `A new contact is assigned to you. check this ${receiver} in your inbox chat`,
          type: 'inbox',
          action: 'inbox-assign',
          contactId: contactId,
        }) as any
      )
      // Refresh team members and user details to get updated assignedContacts
      dispatch(GetAllTeamList({ownerId: OwnerId}) as any)
      dispatch(fetchAsyncUserDetails() as any)
    },
    [dispatch, OwnerId]
  )

  // Unassign conversation using inbox-unassign action
  const unassignConversation = useCallback(
    async (contactId: string) => {
      await dispatch(
        PortalNotifiationBySuperAdmin({
          userTo: [],
          message: '',
          type: 'inbox',
          action: 'inbox-unassign',
          contactId: contactId,
        }) as any
      )
      // Refresh team members and user details to get updated assignedContacts
      dispatch(GetAllTeamList({ownerId: OwnerId}) as any)
      dispatch(fetchAsyncUserDetails() as any)
    },
    [dispatch, OwnerId]
  )

  // Get the team member assigned to a conversation
  const getAssignedMember = useCallback(
    (conversation: ConversationData | null): TeamMember | null => {
      if (!conversation) return null
      const contactId = conversation.contacts._id
      return teamMembers.find((member) => member.assignedContacts?.includes(contactId)) || null
    },
    [teamMembers]
  )

  return {
    // Data
    teamMembers,
    isLoading,

    // Actions
    refresh: fetchMembers,
    assignConversation,
    unassignConversation,
    getAssignedMember,
  }
}
